<template>
  <div class="phone-input relative">
    <slot
      name="label"
    />
    <span class="relative block">
      <div
        class="input-wrapper border-2 border-gray-300 rounded-sm appearance-none
          flex">
        <div
          class="btn-group relative h-45 w-64"
        >
          <li
            v-if="selectedFlag.code !== '' || selectedFlag.prefix !== ''"
            :style="{fontSize: flagWidth}"
            class="dropdown-toggle flex justify-evenly items-center w-64
              text-primary-light text-base bg-white h-full cursor-pointer list-none"
            @click="toggleMenu()"
          >
            <span v-if="showFlags" :class="`flag-icon flag-icon-${selectedFlag.code.toLowerCase()}`"></span>
            <p v-else-if="!showFlags && selectedFlag.prefix" class="text-sm">{{ selectedFlag.prefix }}</p>
            <span class="caret align-middle"></span>
          </li>

          <ul
            v-show="showMenu"
            :class="dropdownClasses"
            class="dropdown-menu w-auto rounded-sm absolute top-full left-0 py-1 shadow
              list-none bg-white z-10 max-h-192 overflow-auto"
          >
            <li
              v-for="(flag, idx) in flags"
              :key="idx"
              class="overflow-hidden w-full relative my-8 px-8"
            >
              <a
                href="javascript:void(0)"
                :style="{fontSize: flagWidth}"
                class="country flex justify-start items-center clear-both font-normal
                  leading-relaxed text-primary-dark whitespace-no-wrap no-underline
                    hover:no-underline hover:text-secondary hover:bg-grey-300"
                @click="updateFlag(flag)"
              >
                <span
                  v-if="showFlags"
                  :class="`flag-icon flag-icon-${flag.countryCode.toLowerCase()}`"
                  class="mr-8"
                ></span>
                <p v-if="showFlags" class="text-sm font-bold mr-8">{{ flag.countryName }}</p>
                <p class="text-sm">{{ flag.prefix }}</p>
              </a>
            </li>
          </ul>
        </div>
        <input
          :value="value"
          type="tel"
          v-masked="value"
          :placeholder="placeholder"
          class="number-field w-full h-45 pr-15 outline-none overflow-hidden rounded-none"
          :class="{'pr-45': isValidNumber}"
          @input="$emit('input', $event.target.value)"
          inputmode="numeric"
        >
        <slot
          name="icon"
          :isValidNumber="isValidNumber"
        />
      </div>
    </span>
    <slot
      name="validation"
      :isValidNumber="isValidNumber"
    />
  </div>
</template>

<script>
// import axios from 'axios';
import 'flag-icon-css/css/flag-icon.min.css';
import { parsePhoneNumberFromString, AsYouType, getCountries } from 'libphonenumber-js/max';
import { getCountryCallingCode, isValidNumber, parseNumber } from 'libphonenumber-js';

export default {
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    countries: {
      type: Array,
      default() {
        return [];
      },
    },
    dropdownClasses: {
      type: String,
      default: '',
    },
    flagWidth: {
      type: String,
      default: '25px',
    },
    showFlags: {
      type: Boolean,
      default: false,
    },
    defaultCountry: {
      type: String,
      default: '',
    },
    validation: {
      type: Boolean,
      default: false,
    },
    setValidPhoneNumber: {
      type: Function,
    },
    setValidCountry: {
      type: Function,
    },
    setInternationalNumber: {
      type: Function,
    },
    authType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      selectedFlag: {
        prefix: '',
        code: '',
        countryName: '',
        numberPlaceholder: '',
      },
      closeOnOutsideClick: true,
      showMenu: false,
      isValidNumber: false,
      phoneCountryCode: '',
    };
  },
  computed: {
    flags() {
      if (this.countries.length) {
        return this.countries;
      }
      const defaultCountries = getCountries();
      defaultCountries.shift(); // remove first country
      const countriesChange = defaultCountries.map((country) => ({
        countryCode: country,
        countryName: '',
        prefix: `+${getCountryCallingCode(country)}`,
      }));
      return countriesChange;
    },
  },

  directives: {
    masked: {
      update(el, binding, vNode) {
        const elem = el;
        const { context } = vNode;
        if (binding.value !== binding.oldValue && binding.oldValue === '') { // for exist phone number
          context.parsePhone(elem, true);
        } else if (binding.value !== binding.oldValue) {
          elem.addEventListener('input', context.parsePhone);
        } else {
          return false;
        }
      },
    },
  },

  mounted() {
    this.setDefaultCountry();
    if (this.closeOnOutsideClick) {
      document.addEventListener('click', this.clickHandler);
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.clickHandler);
  },
  methods: {
    parsePhone(e, existPhone) {
      if (!e.isTrusted && !existPhone) {
        return false;
      }
      let target = e.value;
      if (!existPhone) {
        target = e.currentTarget.value;
      }
      if (this.selectedFlag.code && this.countries.length) {
        const phone = new AsYouType(this.selectedFlag.code).input(target);
        let phoneInInternationalFormat = null;
        if (parsePhoneNumberFromString(phone, this.selectedFlag.code)) {
          const phoneData = parsePhoneNumberFromString(phone, this.selectedFlag.code);
          phoneInInternationalFormat = phoneData.number;
          this.phoneCountryCode = phoneData.country;
          const isValidCountry = this.countries
            .some((passedCountry) => passedCountry.countryCode === phoneData.country);
          this.isValidNumber = phoneData.isValid() && isValidNumber(phone, this.selectedFlag.code)
            && isValidCountry
            && !!target.length;
          this.setValidPhoneNumber(this.isValidNumber);
          if (isValidCountry) {
            this.selectedFlag.code = phoneData.country;
            this.selectedFlag.prefix = `+${phoneData.countryCallingCode}`;
            this.setValidCountry(isValidCountry);
          }
        } else {
          return false;
        }
        if (!existPhone) {
          if (this.isValidNumber && e.currentTarget.value !== '' && this.authType === 'buyer') {
            this.setInternationalNumber(parsePhoneNumberFromString(phone, this.selectedFlag.code)?.number);
            e.currentTarget.value = phone;
          } else {
            e.currentTarget.value = phone;
          }
        } else {
          this.setInternationalNumber(phoneInInternationalFormat || phone);
          e.value = phone;
        }
      } else {
        const phone = parsePhoneNumberFromString(target, this.selectedFlag.code.toUpperCase());
        if (phone) {
          this.selectedFlag.code = phone.country.toLowerCase();
          this.selectedFlag.prefix = `+${phone.countryCallingCode}`;
          this.isValidNumber = isValidNumber(phone.nationalNumber, this.selectedFlag.code.toUpperCase())
            && !!target.length;
          if (target.includes('+')) {
            target = phone.formatInternational();
          } else {
            target = phone.formatNational();
          }
        }
      }

      if (e.dispatchEvent) {
        e.dispatchEvent(new Event('input'));
      }
    },
    setDefaultCountry() {
      if (!this.defaultCountry) {
        const country = this.countries[0];
        this.selectedFlag.code = this.showFlags ? country.countryCode : '';
        this.selectedFlag.prefix = country.prefix;
      } else {
        this.selectedFlag.code = this.defaultCountry;
        this.selectedFlag.prefix = `+${getCountryCallingCode(this.defaultCountry)}`;
      }
    },
    updateFlag(flag) {
      this.selectedFlag.code = flag.countryCode;
      this.selectedFlag.prefix = flag.prefix;
      this.showMenu = false;
      this.setValidCountry(this.selectedFlag.code === this.phoneCountryCode);
      this.$emit('updateFlag', this.selectedFlag);
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    clickHandler(event) {
      const { target } = event;
      const { $el } = this;
      if (!$el.contains(target)) {
        this.showMenu = false;
      }
    },
  },
};
</script>
